/**
 * TanStack Query hooks for membership visit mutations.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services';
import { unwrapServiceResultAsync } from '../services/utils/errorUtils';
import { logger } from '../utils/logger';
import type { MembershipVisitRequest } from '../types';
import { tripByIdQueryKey } from './useTripsQueries';

export function useAddMembershipVisitMutation(tripId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MembershipVisitRequest) =>
      unwrapServiceResultAsync(
        apiService.membershipVisits.addMembershipVisit(tripId, body)
      ),
    onSuccess: () => {
      logger.info('Membership visit added', tripId);
      queryClient.invalidateQueries({ queryKey: tripByIdQueryKey(tripId) });
    },
  });
}

export function useUpdateMembershipVisitMutation(tripId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      visitId,
      body,
    }: {
      visitId: number;
      body: MembershipVisitRequest;
    }) =>
      unwrapServiceResultAsync(
        apiService.membershipVisits.updateMembershipVisit(tripId, visitId, body)
      ),
    onSuccess: () => {
      logger.info('Membership visit updated', tripId);
      queryClient.invalidateQueries({ queryKey: tripByIdQueryKey(tripId) });
    },
  });
}

export function useDeleteMembershipVisitMutation(tripId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visitId: number) =>
      unwrapServiceResultAsync(
        apiService.membershipVisits.deleteMembershipVisit(tripId, visitId)
      ),
    onSuccess: () => {
      logger.info('Membership visit deleted', tripId);
      queryClient.invalidateQueries({ queryKey: tripByIdQueryKey(tripId) });
    },
  });
}

export function useCreateMyMembershipMutation(tripId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrapServiceResultAsync(
        apiService.memberships.createMyMembership(tripId)
      ),
    onSuccess: () => {
      logger.info('Trip membership created', tripId);
      queryClient.invalidateQueries({ queryKey: tripByIdQueryKey(tripId) });
    },
  });
}
